import { useCallback, useRef, useState } from 'react';
import { flushSync } from 'react-dom'; // ✅ CRITICAL: Force synchronous render
import { validateContextWindow } from '../components/chat/upload/ChatFileHandler';
import threadService from '../services/threadService';

// Time the green "ready" tick remains on the preview cards before they vanish
const UPLOAD_READY_FLASH_MS = 300;

/**
 * Hook para manejar el envío de mensajes en el chat
 *
 * ✅ REFACTORED: Now works with React Query's useMessages hook
 * - Removed optimistic update logic (React Query handles it)
 * - Removed chatMessagesHook dependency
 * - Simplified to only handle thread creation and validation
 * - Uses sendMessageFn from useMessages for actual sending
 */
export const useMessageSending = ({
  agentStatus,
  filesWithThumbnails, // ✅ CHANGED: Receive filesWithThumbnails instead of selectedFiles
  clearFiles,          // ✅ CHANGED: Receive clearFiles instead of setSelectedFiles
  // Repone texto y adjuntos cuando el envío falla. El input se limpia ANTES de
  // enviar (para que la tarjeta de progreso no salga duplicada), así que sin
  // esto un envío rechazado —quedarse sin crédito— se lleva el mensaje (#1299).
  restoreDraft,
  currentThread,
  setCurrentThread,
  setThreads,
  setProcessingStatus,
  processingStatus,
  isUploadingFiles,
  setContextValidationError,
  setFileUploadError,
  sendMessageFn, // ✅ NEW: Function from useMessages hook
  waitForWebSocketConnection, // ✅ CRITICAL: Function to wait for WebSocket readiness
  // ✅ STOP state cleanup - new message invalidates previous STOP request
  clearStopStateSync,  // Single function that updates both state and ref synchronously
  pendingRolId,
  consumePendingRolId,
}) => {
  // ✅ FIX WINDOWS: Ref para tracking inmediato sin re-renders
  // Soluciona race condition donde Context tarda ~20-50ms en propagarse en Windows
  const processingThreadRef = useRef({ threadId: null, timestamp: 0 });

  // ✅ FIX: Prevent welcome screen flash when creating first message.
  // Set true before flushSync(setCurrentThread), cleared after sendMessageFn adds optimistic msg.
  // During the flushSync render, currentThread is set but messages is still [] — without this ref,
  // the empty chat view briefly flashes before the optimistic message appears.
  const isTransitioningToFirstMessageRef = useRef(false);

  // Snapshot of the file batch currently being uploaded — drives the progress UI in
  // FilePreviewList. Survives `clearFiles()` so the cards remain visible during XHR.
  // Shape: { items, progress, status, error, controller } | null
  const [uploadingBatch, setUploadingBatch] = useState(null);
  const uploadReadyTimerRef = useRef(null);

  const clearUploadingBatch = useCallback(() => {
    if (uploadReadyTimerRef.current) {
      clearTimeout(uploadReadyTimerRef.current);
      uploadReadyTimerRef.current = null;
    }
    setUploadingBatch(null);
  }, []);

  const cancelUploadingBatch = useCallback(() => {
    setUploadingBatch((prev) => {
      if (!prev) return null;
      try {
        prev.controller?.abort();
      } catch {
        // controller may already be aborted
      }
      return null;
    });
    if (uploadReadyTimerRef.current) {
      clearTimeout(uploadReadyTimerRef.current);
      uploadReadyTimerRef.current = null;
    }
  }, []);

  /**
   * Envía un mensaje al chat, creando un nuevo thread si es necesario
   * @param {string} message - El texto del mensaje a enviar
   * @param {Array<{file: File, thumbnailDataUrl: string | null}>} filesWithThumbnails - Archivos con thumbnails generados
   */
  const handleSendMessage = useCallback(async (message, filesWithThumbnailsParam) => {
    // ✅ CRITICAL: Clear STOP state when user sends new message
    // Previous STOP request is no longer relevant - user decided to continue
    // clearStopStateSync (useTurnLifecycle) updates BOTH state AND ref synchronously
    if (clearStopStateSync) {
      clearStopStateSync();
    }

    // ✅ Extract File objects and metadata from filesWithThumbnails
    const files = filesWithThumbnailsParam.map(item => item.file);
    const filesMetadata = filesWithThumbnailsParam.map(item => ({
      fileName: item.file.name,
      fileType: item.file.type || 'application/octet-stream',
      fileSize: item.file.size,
      thumbnailDataUrl: item.thumbnailDataUrl // Client-side generated thumbnail
    }));

    // 🔍 THUMBNAIL DEBUG: Log what we're sending to backend
    if (filesMetadata.length > 0) {
      console.log('[useMessageSending] 🔍 THUMBNAIL DEBUG - Files metadata being sent:', filesMetadata.map(m => ({
        fileName: m.fileName,
        fileType: m.fileType,
        fileSize: m.fileSize,
        hasThumbnail: !!m.thumbnailDataUrl,
        thumbnailLength: m.thumbnailDataUrl?.length || 0
      })));
    }
    // NO hacer e.preventDefault()
    const isCurrentThreadProcessing = processingStatus[currentThread?.id] || false;

    // ✅ CRITICAL FIX: Agent is only active if BOTH agentName AND currentTask exist
    // When N8N finishes, it sends { agentName: "", currentTask: "" }
    // Empty strings are falsy, so this correctly detects completion
    // Edge case: If N8N sends { agentName: "Copiloto", currentTask: "" } (finished but kept name)
    // this will correctly allow new messages
    const isAgentActive = Boolean(agentStatus?.agentName && agentStatus?.currentTask);

    // Determinar si el agente está activo (cualquiera de las dos fuentes)
    const isAgentOrProcessingActive = isAgentActive || isCurrentThreadProcessing;

    // Validar que hay contenido para enviar y no está procesando
    if ((!message.trim() && files.length === 0) || isAgentOrProcessingActive || isUploadingFiles) {
      console.log('[useMessageSending] Envío bloqueado:', {
        noContent: !message.trim() && files.length === 0,
        isProcessing: isCurrentThreadProcessing,
        agentActive: isAgentActive,
        agentName: agentStatus?.agentName,
        agentTask: agentStatus?.currentTask,
        agentOrProcessingActive: isAgentOrProcessingActive,
        uploading: isUploadingFiles
      });

      // ✅ DEFENSE-IN-DEPTH: Detectar si mensaje llegó a business logic pese a UI bloqueada
      if (isAgentOrProcessingActive) {
        console.warn('[useMessageSending] ⚠️ Message reached business logic despite agent processing - UI layer may have bug');
      }

      return;
    }

    // --- Validación de longitud del mensaje ---
    // Los adjuntos NO se validan aquí: no consumen ventana de contexto (van por RAG, #444).
    try {
      await validateContextWindow(message);
    } catch (err) {
      console.error('[useMessageSending] Error de validación de contexto:', err);
      setContextValidationError('El mensaje es demasiado largo para ser enviado.');
      setTimeout(() => setContextValidationError(null), 5000);
      return;
    }

    let activeThread = currentThread;
    let isNewThread = false;
    let sentMessageContent = message;

    try {
      // Si no hay thread actual, crear uno nuevo
      if (!activeThread) {
        isNewThread = true;
        // ✅ NEW: Usar título temporal simple - el título inteligente se generará en background
        // El backend usará LLM para generar un título basado en el contenido (~300ms después)
        const displayTitle = 'Nuevo Chat';

        // ✅ FIX: Read rolId from state without consuming yet. consumePendingRolId() clears
        // pendingRolId via setState, and the await below yields to React. If React renders
        // mid-await with pendingRolId=null AND currentThread=null, activeAgent becomes null
        // → brief flash of general chat UI. We consume inside flushSync so pendingRolId is
        // cleared in the same synchronous render that sets currentThread.
        const rolId = pendingRolId || null;
        const metadata = rolId ? { rolId } : null;
        console.log('[useMessageSending] Creando nuevo thread con título temporal:', displayTitle, metadata ? `rolId: ${rolId}` : '');
        const newThread = await threadService.createThread(displayTitle, metadata);
        activeThread = newThread;

        // ✅ FIX WINDOWS: Marcar INMEDIATAMENTE en ref (síncronamente accesible)
        processingThreadRef.current = {
          threadId: newThread.id,
          timestamp: Date.now()
        };

        // ✅ CRÍTICO: Marcar como procesando ANTES de setCurrentThread
        // Esto evita race condition donde loadMessages ve processingStatus=false
        setProcessingStatus(prev => ({ ...prev, [newThread.id]: true }));

        // ✅ FIX: Signal transition so Chat.jsx keeps welcome screen visible during flushSync render
        isTransitioningToFirstMessageRef.current = true;

        // ✅ CRITICAL: Force synchronous render so useMessages(newThread.id) runs BEFORE optimistic update
        // Without flushSync, React batches setCurrentThread causing a race condition where:
        // 1. setCurrentThread queued (async)
        // 2. optimistic message added to cache ['messages', newThreadId]
        // 3. Component renders with useMessages(newThreadId) AFTER optimistic update
        // Result: Optimistic message already in cache but component wasn't subscribed to changes
        flushSync(() => {
          setCurrentThread(newThread); // Esto dispara el useEffect que carga mensajes
          if (consumePendingRolId) consumePendingRolId(); // Clear pendingRolId atomically with thread set
          // ✅ FIX: Check for duplicates before adding (same pattern as useN8NDaemonHandler.js:25)
          setThreads(prev => {
            if (prev.some(t => t.id === newThread.id)) {
              console.warn('[useMessageSending] ⚠️ Thread already exists, skipping duplicate:', newThread.id);
              return prev;
            }
            return [newThread, ...prev];
          });
        });
        // Now component is rendered and useMessages(newThread.id) is actively subscribed to cache

        // ✅ PERFORMANCE FIX: Start WebSocket connection in background (fire and forget)
        // Don't wait for it - the connection happens in parallel with message sending
        // N8N responses will arrive via WebSocket whenever it's ready
        if (waitForWebSocketConnection) {
          // Fire and forget - let it connect in background
          waitForWebSocketConnection(5000).then(() => {
            console.log('[useMessageSending] ✅ WebSocket ready for N8N responses');
          }).catch((err) => {
            console.warn('[useMessageSending] ⚠️ WebSocket connection issue (non-blocking):', err.message);
          });
        }
      }

      // Asegurarnos de tener un ID de hilo activo
      if (!activeThread?.id) {
        throw new Error("No se pudo obtener o crear un ID de hilo activo.");
      }

      // Marcar este hilo como procesando (solo si no es nuevo, ya lo marcamos antes)
      if (!isNewThread) {
        // ✅ FIX WINDOWS: Marcar en ref también para threads existentes
        processingThreadRef.current = {
          threadId: activeThread.id,
          timestamp: Date.now()
        };
        setProcessingStatus(prev => ({ ...prev, [activeThread.id]: true }));
      }

      // Snapshot the batch BEFORE clearing so the preview cards keep showing the
      // files (now with a progress overlay) while the XHR uploads them.
      const hasFiles = files.length > 0;
      const controller = hasFiles && typeof AbortController !== 'undefined'
        ? new AbortController()
        : null;
      if (hasFiles) {
        // Mirror the FileWithThumbnail shape used by FilePreviewList
        const items = filesWithThumbnailsParam.map((entry) => ({
          file: entry.file,
          thumbnailDataUrl: entry.thumbnailDataUrl,
        }));
        setUploadingBatch({
          items,
          progress: 0,
          status: 'uploading',
          error: null,
          controller,
        });
      }

      // Limpiar el input (archivos)
      clearFiles(); // ✅ CHANGED: Use hook's clearFiles method

      // ✅ CRITICAL: Send message IMMEDIATELY - NO blocking waits
      // - Optimistic update happens instantly (React Query)
      // - Backend API call happens immediately
      // - WebSocket connection in background (fire and forget)
      // - N8N response arrives via WebSocket whenever ready
      console.log('[useMessageSending] Calling sendMessageFn with:', {
        content: message,
        files,
        threadId: activeThread.id,
        isNewThread
      });
      sendMessageFn(
        {
          content: message,
          files: files,
          filesMetadata: filesMetadata, // ✅ NEW: Pass file metadata for optimistic update
          // ✅ CRITICAL: Always pass overrideThreadId to ensure correct cache targeting
          // For existing threads this matches currentThread, for new threads it's the fresh ID
          overrideThreadId: activeThread.id,
          onProgress: hasFiles
            ? (pct) => {
                setUploadingBatch((prev) => (
                  prev && prev.status === 'uploading'
                    ? { ...prev, progress: pct }
                    : prev
                ));
              }
            : undefined,
          signal: controller?.signal,
        },
        {
          onSuccess: () => {
            if (!hasFiles) return;
            setUploadingBatch((prev) => (
              prev ? { ...prev, status: 'success', progress: 100 } : prev
            ));
            if (uploadReadyTimerRef.current) clearTimeout(uploadReadyTimerRef.current);
            uploadReadyTimerRef.current = setTimeout(() => {
              setUploadingBatch(null);
              uploadReadyTimerRef.current = null;
            }, UPLOAD_READY_FLASH_MS);
          },
          // Estos callbacks se pasan SIEMPRE, no sólo cuando hay adjuntos. El
          // `try/catch` de esta función no los cubre: `sendMessageFn` es una
          // mutación que se despacha, no se espera, así que su rechazo no sube
          // por ahi. Mientras esto solo se cableaba con `hasFiles`, un envio de
          // texto contra el tope no producia NADA en pantalla (#1299).
          onError: (err) => {
            if (err?.code === 'UPLOAD_ABORTED') {
              setUploadingBatch(null);
              return;
            }

            // Sin crédito no es un fallo de subida: el fichero viajó bien. El
            // aviso con la salida al pago lo levanta BillingContext al recibir
            // la señal; aquí solo se deja de mentir sobre la causa.
            const outOfCredit = err?.status === 402 && err?.message === 'usage_limit_exceeded';
            // #1294: dos 402/503 que NO son «sin crédito» y cuyo texto tiene que decirlo.
            const docLimit = outOfCredit && err?.code === 'trial_doc_limit';
            // El texto del 503 lo pone el backend: distingue «inténtalo luego» de «tu cuenta».
            const noStreaming = err?.code === 'TRIAL_REQUIRES_STREAMING';
            const failureText = docLimit
              ? 'Has usado todos los documentos de la prueba. Las cuentas de pago no tienen este límite.'
              : noStreaming
                ? err.message
                : outOfCredit ? 'Te has quedado sin crédito.' : null;

            // El envío no llegó a ocurrir: devuélvele lo que había escrito y
            // adjuntado. Vale para cualquier fallo, no sólo el de crédito — un
            // corte de red se llevaba el mensaje exactamente igual.
            restoreDraft?.(message, filesWithThumbnailsParam);

            setProcessingStatus(prev => ({ ...prev, [activeThread.id]: false }));
            setUploadingBatch((prev) => (
              prev
                ? {
                    ...prev,
                    status: 'error',
                    error: docLimit ? 'Límite de documentos' : outOfCredit ? 'Sin crédito' : (failureText || err?.message || 'Error al subir el archivo'),
                  }
                : prev
            ));

            if (!hasFiles || outOfCredit || noStreaming) {
              setFileUploadError(failureText || 'No se pudo enviar el mensaje. Intenta de nuevo.');
              setTimeout(() => setFileUploadError(null), 5000);
            }
          },
        }
      );

      // ✅ FIX: Clear transition flag. sendMessageFn dispatches the mutation synchronously,
      // but onMutate is async (it awaits cancelQueries). The optimistic message lands in
      // the cache as a microtask AFTER this line — but React re-renders are also scheduled
      // (not synchronous), so the ref is always false by the time useMemo re-evaluates
      // with the new messages array. Do NOT add flushSync inside onMutate.
      isTransitioningToFirstMessageRef.current = false;

    } catch (error) {
      console.error('[useMessageSending] Error al enviar mensaje:', error);

      // ✅ FIX: Clear transition flag on error to avoid stuck welcome screen
      isTransitioningToFirstMessageRef.current = false;

      // Resetear estado de procesamiento si hay error crítico
      if (activeThread?.id) {
        setProcessingStatus(prev => ({ ...prev, [activeThread.id]: false }));
      }
      
      // Mostrar error en UI
      setFileUploadError('No se pudo enviar el mensaje. Intenta de nuevo.');
      setTimeout(() => setFileUploadError(null), 5000);
    }
  }, [
    agentStatus,
    currentThread,
    processingStatus,
    isUploadingFiles,
    clearFiles, // ✅ CHANGED: Updated dependency
    restoreDraft,
    setCurrentThread,
    setThreads,
    setProcessingStatus,
    setContextValidationError,
    setFileUploadError,
    sendMessageFn, // ✅ REPLACED: chatMessagesHook → sendMessageFn
    waitForWebSocketConnection, // ✅ NEW: WebSocket wait function
    clearStopStateSync,  // ✅ STOP cleanup on new message (state + ref)
    pendingRolId, // ✅ FIX: Read rolId without consuming to avoid mid-await flash
    consumePendingRolId, // ✅ FIX: Was missing — caused stale closure when clicking agent from null currentThread
  ]);

  /**
   * Determina si el agente está actualmente activo
   * Usa TRIPLE fuente de verdad:
   * 1. agentStatus.agentName Y agentStatus.currentTask (backend via WebSocket): Estado confirmado del agente
   * 2. processingThreadRef (ref local con timeout): Tracking inmediato para Windows (soluciona race condition)
   * 3. processingStatus (Context): Estado persistente y coordinación entre componentes
   */
  const isAgentCurrentlyActive =
    (Boolean(agentStatus?.agentName) && Boolean(agentStatus?.currentTask)) ||
    (
      processingThreadRef.current.threadId === currentThread?.id &&
      Date.now() - processingThreadRef.current.timestamp < 5000  // 5s auto-cleanup
    ) ||
    Boolean(processingStatus[currentThread?.id]);

  /**
   * Limpia el ref de procesamiento inmediato
   * Usado cuando llega un mensaje final o se detecta que el agente terminó
   * Esto fuerza que isAgentCurrentlyActive sea false inmediatamente
   */
  const clearProcessingThreadRef = useCallback(() => {
    console.log('[useMessageSending] 🧹 Clearing processingThreadRef');
    processingThreadRef.current = { threadId: null, timestamp: 0 };
  }, []);

  return {
    handleSendMessage,
    isAgentCurrentlyActive,
    clearProcessingThreadRef,  // ✅ Export function to clear ref
    isTransitioningToFirstMessageRef,  // ✅ FIX: Expose ref so Chat.jsx can prevent welcome screen flash
    uploadingBatch,
    cancelUploadingBatch,
    clearUploadingBatch,
  };
};